import ErrorState from '@rio-cloud/rio-uikit/ErrorState';
import Spinner from '@rio-cloud/rio-uikit/Spinner';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { isUserSessionExpired } from '../../../../configuration/login/loginSlice';
import { useAppSelector } from '../../../../configuration/setup/hooks';
import { useGetServiceOrdersQuery } from '../../api/serviceOrdersApi';
import {
    getDebouncedServiceOrderSearch,
    getServiceOrderCategory,
    getServiceOrderModalFilters,
    getServiceOrderSort,
    getServiceOrderStatusFilter,
} from '../../reducers/serviceOrdersSlice';
import { LoadMoreButton } from '../../sharedComponents/table/LoadMoreButton';
import { useSetUserTriggeredFetching } from '../../sharedComponents/userTriggeredFetchingHook';
import { ServiceOrderCardsSorting } from './table/ServiceOrderCardsSorting';
import { ServiceOrderTable } from './table/ServiceOrdersTable';
import { ServiceCategory } from '../shared/commonServiceOrder.types';
import { ServiceOrderTableToolbar } from '../shared/toolbar/ServiceOrderTableToolbar';
import { ServiceOrderStatusFilter } from './table/toolbar/ServiceOrderStatusFilter';
import { ServiceOrderExportModal } from './table/toolbar/ServiceOrderExportModal';
import { TableColumnSettingsState } from '../../sharedComponents/table/TableColumnSettingsState';
import { serviceOrderColumnDefinition, ServiceOrdersTableColumns } from './table/ServiceOrdersTableColumns';

export const DEFAULT_SERVICE_ORDERS_LIMIT_SIZE = 100;
export const MAX_SERVICE_ORDERS_LIMIT_SIZE = 1000;

export const ServiceOrders = () => {
    const isSessionExpired = useAppSelector(isUserSessionExpired);
    const statusFilter = useAppSelector(getServiceOrderStatusFilter);
    const modalFilters = useAppSelector(getServiceOrderModalFilters);
    const debouncedSearchTerm = useAppSelector(getDebouncedServiceOrderSearch).trim();
    const category = useAppSelector(getServiceOrderCategory);
    const sort = useAppSelector(getServiceOrderSort);
    const [limit, setLimit] = useState(DEFAULT_SERVICE_ORDERS_LIMIT_SIZE);
    const nextLimit = limit + DEFAULT_SERVICE_ORDERS_LIMIT_SIZE;

    const isArchivedView = category === ServiceCategory.ARCHIVE;
    const {
        data: serviceOrderPage,
        isFetching,
        isLoading,
        isError,
    } = useGetServiceOrdersQuery(
        {
            location: modalFilters.locationFilter,
            statuses: statusFilter,
            serviceTypes: modalFilters.serviceTypeFilter,
            orderIssuedAtAfter: modalFilters.startDateFilter,
            orderIssuedAtBefore: modalFilters.endDateFilter,
            searchTerm: debouncedSearchTerm.length === 0 ? undefined : debouncedSearchTerm,
            sortBy: sort,
            archived: isArchivedView,
            limit,
        },
        {
            pollingInterval: isSessionExpired ? undefined : 60000,
        },
    );
    useSetUserTriggeredFetching({
        isFetching,
        depsWhichTriggerUserFetching: [statusFilter, modalFilters, debouncedSearchTerm, sort, category],
    });

    if (isLoading) {
        return (
            <div data-testid="spinner">
                <Spinner />
            </div>
        );
    }

    if (isError || typeof serviceOrderPage === 'undefined') {
        return (
            <ErrorState
                headline={<FormattedMessage id="intl-msg:common-message.error.generic.headline" />}
                message={<FormattedMessage id="intl-msg:common-message.error.generic.message" />}
            />
        );
    }

    const serviceOrders = serviceOrderPage.items;

    const columnSettingTableId = isArchivedView ? 'serviceOrder.archived' : 'serviceOrder.order';
    const columnDefinition = getColumnDefinition(isArchivedView);
    return (
        <TableColumnSettingsState
            key={columnSettingTableId}
            tableId={columnSettingTableId}
            columnDefinition={columnDefinition}
        >
            <ServiceOrderTableToolbar
                statusFilter={<ServiceOrderStatusFilter />}
                exportModal={<ServiceOrderExportModal />}
            />
            <ServiceOrderCardsSorting />
            <ServiceOrderTable serviceOrders={serviceOrders} />
            {serviceOrders.length > 0 && (
                <LoadMoreButton
                    onLoadMore={() => setLimit(nextLimit)}
                    hasMoreToLoad={serviceOrderPage.hasMore}
                    limitReached={nextLimit > MAX_SERVICE_ORDERS_LIMIT_SIZE}
                />
            )}
        </TableColumnSettingsState>
    );
};

const getColumnDefinition = (isArchivedView: boolean) =>
    serviceOrderColumnDefinition.filter(
        (def) => !isArchivedView || def.column !== ServiceOrdersTableColumns.ACTION_BUTTON,
    );
